import { jsx } from "react/jsx-runtime";
import { cva } from "@/styled-system/css";
import { List as List$1 } from "@zillow/constellation-10";
import React from "react";
import { resolveSpacing } from "../../utils/token-migration/migrate-responsive.esm.js";
import { migrateSpacingToken } from "../../utils/token-migration/v8-to-v10.esm.js";
const listRecipe = cva({
  variants: {
    marginBlockStart: {
      none: {
        marginBlockStart: "none"
      },
      tightest: {
        marginBlockStart: "tightest"
      },
      tighter: {
        marginBlockStart: "tighter"
      },
      tight: {
        marginBlockStart: "tight"
      },
      default: {
        marginBlockStart: "default"
      },
      loose: {
        marginBlockStart: "loose"
      },
      looser: {
        marginBlockStart: "looser"
      },
      "layout.tightest": {
        marginBlockStart: "layout.tightest"
      },
      "layout.tighter": {
        marginBlockStart: "layout.tighter"
      },
      "layout.tight": {
        marginBlockStart: "layout.tight"
      },
      "layout.default": {
        marginBlockStart: "layout.default"
      },
      "layout.loose": {
        marginBlockStart: "layout.loose"
      },
      "layout.looser": {
        marginBlockStart: "layout.looser"
      },
      "layout.loosest": {
        marginBlockStart: "layout.loosest"
      },
      "layout.vast.default": {
        marginBlockStart: "layout.vast.default"
      },
      "layout.vast.loose": {
        marginBlockStart: "layout.vast.loose"
      },
      "layout.vast.looser": {
        marginBlockStart: "layout.vast.looser"
      },
      // Negative margins — the editor's SpacingControls offers -xs…-xxxl for
      // margin attributes, and migrateSpacingToken maps them to negative v10
      // tokens (NEGATIVE_SPACING_MAP). Mirror them here so the string path
      // emits CSS instead of silently dropping the value.
      "-tightest": {
        marginBlockStart: "-tightest"
      },
      "-tighter": {
        marginBlockStart: "-tighter"
      },
      "-default": {
        marginBlockStart: "-default"
      },
      "-loose": {
        marginBlockStart: "-loose"
      },
      "-looser": {
        marginBlockStart: "-looser"
      },
      "-layout.looser": {
        marginBlockStart: "-layout.looser"
      },
      "-layout.vast.default": {
        marginBlockStart: "-layout.vast.default"
      },
      "-layout.vast.loose": {
        marginBlockStart: "-layout.vast.loose"
      }
    },
    marginBlockEnd: {
      none: {
        marginBlockEnd: "none"
      },
      tightest: {
        marginBlockEnd: "tightest"
      },
      tighter: {
        marginBlockEnd: "tighter"
      },
      tight: {
        marginBlockEnd: "tight"
      },
      default: {
        marginBlockEnd: "default"
      },
      loose: {
        marginBlockEnd: "loose"
      },
      looser: {
        marginBlockEnd: "looser"
      },
      "layout.tightest": {
        marginBlockEnd: "layout.tightest"
      },
      "layout.tighter": {
        marginBlockEnd: "layout.tighter"
      },
      "layout.tight": {
        marginBlockEnd: "layout.tight"
      },
      "layout.default": {
        marginBlockEnd: "layout.default"
      },
      "layout.loose": {
        marginBlockEnd: "layout.loose"
      },
      "layout.looser": {
        marginBlockEnd: "layout.looser"
      },
      "layout.loosest": {
        marginBlockEnd: "layout.loosest"
      },
      "layout.vast.default": {
        marginBlockEnd: "layout.vast.default"
      },
      "layout.vast.loose": {
        marginBlockEnd: "layout.vast.loose"
      },
      "layout.vast.looser": {
        marginBlockEnd: "layout.vast.looser"
      },
      // Negative margins — see marginBlockStart above.
      "-tightest": {
        marginBlockEnd: "-tightest"
      },
      "-tighter": {
        marginBlockEnd: "-tighter"
      },
      "-default": {
        marginBlockEnd: "-default"
      },
      "-loose": {
        marginBlockEnd: "-loose"
      },
      "-looser": {
        marginBlockEnd: "-looser"
      },
      "-layout.looser": {
        marginBlockEnd: "-layout.looser"
      },
      "-layout.vast.default": {
        marginBlockEnd: "-layout.vast.default"
      },
      "-layout.vast.loose": {
        marginBlockEnd: "-layout.vast.loose"
      }
    }
  }
});
const List = ({
  appearance,
  verticalSpace,
  adornmentColor: _adornmentColor,
  // not supported by v10 List.Root
  marginTop,
  marginBottom,
  paddingBottom,
  children,
  ...rest
}) => {
  const resolvedMarginTop = resolveSpacing(marginTop);
  const resolvedMarginBottom = resolveSpacing(marginBottom);
  const recipeProps = {};
  const responsiveCss = {};
  if (typeof resolvedMarginTop === "string") {
    recipeProps.marginBlockStart = resolvedMarginTop;
  } else if (resolvedMarginTop) {
    responsiveCss.marginBlockStart = resolvedMarginTop;
  }
  if (typeof resolvedMarginBottom === "string") {
    recipeProps.marginBlockEnd = resolvedMarginBottom;
  } else if (resolvedMarginBottom) {
    responsiveCss.marginBlockEnd = resolvedMarginBottom;
  }
  const resolvedVerticalSpace = verticalSpace ? migrateSpacingToken(verticalSpace) : void 0;
  const resolvedPaddingBottom = paddingBottom ? migrateSpacingToken(paddingBottom) : void 0;
  const isCheckmark = appearance === "checkmark";
  const v10Appearance = isCheckmark || appearance === "horizontalTable" ? "none" : appearance;
  const cssProps = {
    // v10 List.Root sets color: 'List.label' as a base style, overriding parent
    // color inheritance. Force inherit so lists inside colored Groups (e.g. white
    // text on dark backgrounds) render correctly — mirrors the v8 color: 'inherit'
    // applied via &&&&& specificity on StyledList.
    color: "inherit",
    ...listRecipe.raw(recipeProps),
    ...responsiveCss,
    ...isCheckmark ? {
      listStyleType: '"✓  "'
    } : {},
    ...resolvedVerticalSpace && resolvedVerticalSpace !== "none" ? {
      rowGap: resolvedVerticalSpace
    } : {},
    ...resolvedPaddingBottom ? {
      paddingBlockEnd: resolvedPaddingBottom
    } : {}
  };
  return /* @__PURE__ */ jsx(List$1.Root, { appearance: v10Appearance, css: Object.keys(cssProps).length > 0 ? cssProps : void 0, ...rest, children });
};
const ListItem = React.forwardRef(({
  css: cssProp,
  ...rest
}, ref) => {
  const itemCss = Object.assign({
    paddingBlock: "none"
  }, cssProp);
  return /* @__PURE__ */ jsx(List$1.Item, { ref, css: itemCss, ...rest });
});
ListItem.displayName = "List.Item";
List.Item = ListItem;
export {
  List as default
};
